const AUTH_TOKEN_KEY = 'mindvrik_auth_token';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

export const REPORT_STATUSES = {
  waitingForAssessment: 'waiting_for_assessment',
  assessmentSubmitted: 'assessment_submitted',
  reportGenerating: 'report_generating',
  counselorReviewPending: 'counselor_review_pending',
  reportReady: 'report_ready',
  sharedWithClient: 'shared_with_client',
};

export const reportStatusLabels = {
  [REPORT_STATUSES.waitingForAssessment]: 'Waiting for Assessment',
  [REPORT_STATUSES.assessmentSubmitted]: 'Assessment Submitted',
  [REPORT_STATUSES.reportGenerating]: 'Report Generating',
  [REPORT_STATUSES.counselorReviewPending]: 'Counselor Review Pending',
  [REPORT_STATUSES.reportReady]: 'Report Ready',
  [REPORT_STATUSES.sharedWithClient]: 'Shared with Client',
};

const request = async (path, options = {}) => {
  const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(payload?.detail || payload?.message || 'Request failed. Please try again.');
  }
  return payload;
};

const normalizeReport = (report, user) => {
  const sections = report?.content?.sections || [];
  return {
    id: report?.id,
    studentName: report?.content?.studentName || user?.studentName || 'Student',
    generatedDate: report?.generatedAt || report?.createdAt || null,
    status: report?.status || REPORT_STATUSES.waitingForAssessment,
    counselorReviewStatus: report?.counselorReviewStatus || 'pending',
    sections: sections.map((section) => [section.title, section.text]),
  };
};

export const reportService = {
  async fetchReportStatus(user) {
    if (!user) return REPORT_STATUSES.waitingForAssessment;
    try {
      const report = await request('/reports/me');
      return report.status || REPORT_STATUSES.waitingForAssessment;
    } catch (error) {
      return REPORT_STATUSES.waitingForAssessment;
    }
  },

  async fetchReportByUser(user) {
    if (!user) throw new Error('Login is required to view reports.');
    const report = await request('/reports/me');
    return normalizeReport(report, user);
  },

  async downloadReport(user) {
    const report = await this.fetchReportByUser(user);
    const content = [
      `MindVrik Advisory Report - ${report.studentName}`,
      `Status: ${reportStatusLabels[report.status] || report.status}`,
      `Generated: ${report.generatedDate || 'Not generated yet'}`,
      '',
      ...report.sections.map(([title, text]) => `${title}\n${text}\n`),
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `MindVrik-report-${report.studentName.replace(/\s+/g, '-').toLowerCase()}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
    return { message: 'Report downloaded.' };
  },

  async shareReportWithClient() {
    return {
      user: null,
      message: 'Report sharing email/WhatsApp will be connected in the notification phase.',
    };
  },

  async updateReportStatus(user, status) {
    if (!user?.assessmentSessionId) throw new Error('Assessment session is required to update report status.');
    return request(`/assessments/sessions/${user.assessmentSessionId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
};
